/**
 * Fehler, wie die Oberfläche sie kennt.
 *
 * Ein roher fetch-Fehler erreicht nie einen Bildschirm. Jeder Fehler hat eine
 * Art, eine deutsche Meldung, und die Angabe, ob ein erneuter Versuch Sinn
 * ergibt. Das ist die Grundlage für die Regel "keine rohen Netzwerkfehler".
 */

export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'validation'
  | 'conflict'
  | 'server'
  | 'unknown'

export interface FieldProblem {
  feld: string
  problem: string
}

export class ApiError extends Error {
  constructor(
    public readonly kind: ApiErrorKind,
    message: string,
    public readonly options: {
      status?: number
      code?: string
      fields?: FieldProblem[]
      /** Ist ein erneuter Versuch sinnvoll, oder ändert sich ohne Zutun nichts? */
      retryable: boolean
      /**
       * Der Zustand ist nicht feststellbar – die Aktion kann ausgeführt worden
       * sein oder nicht. Nie blind wiederholen, sondern nachfragen.
       */
      unclear?: boolean
      /**
       * Der Grund, den der Server in `details.grund` mitgibt.
       *
       * Heute genau ein Fall: `STEP_UP_NOETIG`. Die Oberflaeche soll darauf
       * den Bestaetigungsdialog oeffnen statt eine Fehlermeldung anzuzeigen –
       * ein fehlender Sicherheitsnachweis ist kein Fehler des Benutzers,
       * sondern ein Zwischenschritt.
       */
      grund?: string
      /**
       * Die uebrigen Angaben aus `details` – rohe Werte, ohne Deutung.
       *
       * Gebraucht fuer genau einen Fall, und der ist der Grund fuer dieses
       * Feld: Ein stillgelegter Betrieb kommt bei der Anmeldung nicht durch,
       * also kann die Sperrseite ihre Angaben nirgends anders holen als aus
       * der Absage selbst – Grund der Sperre, Datum, ob eine Zahlung noch
       * etwas aendern kann.
       *
       * Bewusst `unknown` je Wert: Was der Server mitgibt, wird dort geprueft
       * und hier nicht nachgeraten. Wer es liest, prueft den Typ selbst.
       */
      angaben?: Record<string, unknown>
    }
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Siehe `options.grund`. */
  get grund(): string | undefined {
    return this.options.grund
  }

  /** Siehe `options.angaben`. */
  get angaben(): Record<string, unknown> | undefined {
    return this.options.angaben
  }

  get retryable(): boolean {
    return this.options.retryable
  }
  get unclear(): boolean {
    return this.options.unclear === true
  }
  get status(): number | undefined {
    return this.options.status
  }
}

/** Deutsche Standardmeldungen. Sie sagen, was als Nächstes zu tun ist. */
export const KIND_TEXT: Record<ApiErrorKind, string> = {
  network:
    'Keine Verbindung. Deine Eingabe ist gespeichert und geht raus, sobald du wieder Empfang hast.',
  timeout: 'Der Server hat nicht rechtzeitig geantwortet. Ich prüfe, ob es trotzdem geklappt hat.',
  unauthorized: 'Deine Anmeldung ist abgelaufen. Bitte melde dich neu an.',
  forbidden: 'Dafür fehlt dir die Berechtigung.',
  validation: 'Die Eingaben sind unvollständig oder nicht gültig.',
  conflict: 'Der Eintrag wurde zwischenzeitlich geändert. Bitte neu laden.',
  server: 'Auf dem Server ist etwas schiefgelaufen. Ich versuche es gleich nochmal.',
  unknown: 'Da ist etwas schiefgelaufen.'
}

/** Aus HTTP-Status die Fehlerart ableiten. */
export function kindFromStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 409) return 'conflict'
  if (status === 408 || status === 429) return 'timeout'
  if (status === 400 || status === 422) return 'validation'
  if (status >= 500) return 'server'
  return 'unknown'
}

/**
 * Zentrale Regel für den Ausgangskorb:
 * 4xx ausser 408 und 429 sind dauerhaft – wiederholen ändert nichts.
 * 408, 429, 5xx, Zeitüberschreitung und fehlendes Netz sind vorübergehend.
 */
export function isTransient(status: number | undefined, kind: ApiErrorKind): boolean {
  if (kind === 'network' || kind === 'timeout' || kind === 'server') return true
  if (status === undefined) return kind === 'unknown' ? false : true
  if (status === 408 || status === 429) return true
  if (status >= 500) return true
  return false
}

/**
 * Ein Fehler als Satz, den man in ein Formular schreiben kann.
 *
 * notifyError wirft dasselbe in eine Meldung, die nach Sekunden verschwindet.
 * Für ein Formular ist das zu wenig: wer sich vertippt hat, schaut auf das
 * Feld und nicht in die Ecke, in der eben etwas aufblitzte. Deshalb dieselbe
 * Aufbereitung an einer Stelle – und zwei Orte, die sie anzeigen.
 */
export function fehlerText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.kind === 'validation' && err.options.fields?.length) {
      return `${err.message} ${err.options.fields.map((f) => f.problem).join(' · ')}`
    }
    return err.message
  }
  if (err instanceof Error && err.message) return err.message
  return 'Da ist etwas schiefgelaufen. Bitte nochmals versuchen.'
}
